/**
 * HintBubble Component
 * 
 * Renders a coaching hint with type-based styling (suggestion/warning/praise)
 */

interface HintBubbleProps {
  text: string;
  type: 'suggestion' | 'warning' | 'praise';
  followed?: boolean;
  autoResolved?: boolean; // NEW: Hint was resolved without being opened
}

export default function HintBubble({ text, type, followed = false, autoResolved = false }: HintBubbleProps) {
  // Define colors and icons based on hint type
  const typeConfig = {
    suggestion: {
      bg: 'bg-amber-500/40',
      border: 'border-amber-300/30',
      badgeBg: 'bg-amber-600/80',
      textTitle: 'text-amber-100',
      textBody: 'text-amber-50',
      icon: '💡',
      badgeIcon: '💡',
      title: 'Coaching Hint'
    },
    warning: {
      bg: 'bg-red-500/40',
      border: 'border-red-300/30',
      badgeBg: 'bg-red-600/80',
      textTitle: 'text-red-100',
      textBody: 'text-red-50',
      icon: '⚠️',
      badgeIcon: '⚠',
      title: 'Warning'
    },
    praise: {
      bg: 'bg-green-500/40',
      border: 'border-green-300/30',
      badgeBg: 'bg-green-600/80',
      textTitle: 'text-green-100',
      textBody: 'text-green-50',
      icon: '✨',
      badgeIcon: '✨',
      title: 'Great!'
    }
  };

  const config = typeConfig[type] || typeConfig.suggestion;

  return (
    <div className="flex items-start gap-2 md:gap-3 justify-end pr-3 md:pr-8">
      <div className={`p-2 md:p-3 rounded-lg backdrop-blur-sm border max-w-[85%] md:max-w-lg transition-colors duration-500 ${
        followed
          ? 'bg-green-500/40 border-green-300/30'
          : `${config.bg} ${config.border}`
      }`}>
        <div className="flex items-start gap-2">
          <span className="text-lg md:text-xl">{followed ? '✅' : config.icon}</span>
          <div className="flex-1">
            <div className={`font-semibold text-xs md:text-sm mb-1 ${followed ? 'text-green-100' : config.textTitle}`}>
              {followed ? (autoResolved ? '🎯 Resolveste Sozinho!' : 'Nice!') : config.title}
            </div>
            <span className={`text-xs md:text-sm ${followed ? 'text-green-50' : config.textBody}`}>
              {text}
            </span>
            {autoResolved && (
              <div className="text-[10px] md:text-xs text-green-200/80 mt-1 italic">
                Completaste isto antes de precisares da dica
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[9px] md:text-xs font-bold transition-colors duration-500 ${
        followed
          ? 'bg-green-600/80'
          : config.badgeBg
      }`}>
        {followed ? '✓' : config.badgeIcon}
      </div>
    </div>
  );
}

